import { useEffect } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth } from './context/AuthContext';

// Track ongoing refresh promise to prevent multiple simultaneous refreshes
let refreshPromise: Promise<string | null> | null = null;

export const useConfigureAxios = () => {
  const { accessToken, refreshToken, forceLogout, isRefreshing, showError, sessionState } = useAuth();
  
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(async config => {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    }, error => Promise.reject(error));

    const responseInterceptor = axios.interceptors.response.use(
      response => response, 
      async error => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized errors (token expired)
        if (error.response?.status === 401 && !originalRequest._retry) {
          console.log('Received 401 error, attempting token refresh');
          originalRequest._retry = true;

          try {
            // Use shared refresh promise to prevent multiple simultaneous refreshes
            if (!refreshPromise) {
              refreshPromise = refreshToken();
            }

            const newToken = await refreshPromise;
            refreshPromise = null; // Clear the promise after completion

            if (newToken) {
              console.log('Token refreshed, retrying original request');
              
              // Update the authorization header for the retry
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${newToken}`
              };

              // Retry the original request with the new token
              return axios(originalRequest);
            } else {
              console.log('Token refresh failed, redirecting to login');
              return Promise.reject(error);
            }
          } catch (refreshError) {
            console.error('Token refresh error:', refreshError);
            refreshPromise = null; // Clear the promise on error
            
            // If refresh fails, the forceLogout will be called by the refreshToken function
            return Promise.reject(error);
          }
        }

        // Handle 403 Forbidden errors (insufficient permissions)
        if (error.response?.status === 403) {
          console.log('Received 403 error - insufficient permissions');
          showError('Access denied. You don\'t have permission to perform this action.');
          return Promise.reject(error);
        }

        // Handle 500 Internal Server errors
        if (error.response?.status === 500) {
          console.log('Received 500 error - server error');
          showError('Server error occurred. Please try again later.');
          return Promise.reject(error);
        }

        // Handle 502 Bad Gateway
        if (error.response?.status === 502) {
          console.log('Received 502 error - bad gateway');
          showError('Service temporarily unavailable. Please try again.');
          return Promise.reject(error);
        }

        // Handle 503 Service Unavailable
        if (error.response?.status === 503) {
          console.log('Received 503 error - service unavailable');
          showError('Service temporarily unavailable. Please try again later.');
          return Promise.reject(error);
        }

        // Handle network errors (no response)
        if (!error.response) {
          console.log('Network error - no response received');
          if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
            showError('Network connection problem. Please check your internet connection.');
          } else {
            showError('Connection error. Please check your network and try again.');
          }
          return Promise.reject(error);
        }

        // Handle other HTTP errors (400, 404, etc.)
        if (error.response?.status >= 400 && error.response?.status < 500) {
          console.log(`Received ${error.response.status} error`);
          const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               `Request failed (${error.response.status})`;
          showError(errorMessage);
        }

        // For other errors or if retry already attempted, reject as normal
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [accessToken, refreshToken, forceLogout, isRefreshing, sessionState]);
};
